import { Button, Grid2, Stack } from "@mui/material";
import ContainerLoading from "../components/ContainerLoading";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Input } from "../components/Input";
import { useNavigate } from "react-router";
import PageHeader from "../components/PageHeader";
import { registerStoreProducts } from "../services/products";
import {
  formatNumber,
  formatPrice,
  maskMoney,
  unMaskMoney,
} from "../helpers/format";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import DropComponent from "../components/Dropzone";
import * as Yup from "yup";
import { isEmpty } from "lodash";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Nome é obrigatório"),
  price: Yup.string()
    .required("Preço é obrigatório")
    .test(
      "priceValide",
      "Preço precisa ser maior que zero",
      (value) => Number(unMaskMoney(value)) > 0 // Using card-validator
    ),
  sku: Yup.string().required("SKU é obrigatório"),
  qty: Yup.string().required("Estoque é obrigatório"),
});

const ProductsRegister = () => {
  const navigate = useNavigate();
  const [imgs, setImgs] = useState<(File | string)[]>([]);

  const { mutate, isPending } = useMutation({
    mutationFn: registerStoreProducts,
    onSuccess: () => {
      toast.success("Produto cadastrado");
      navigate("/products");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const {
    values,
    touched,
    errors,
    handleSubmit,
    setFieldValue,
    handleBlur,
    handleChange,
  } = useFormik({
    initialValues: {
      name: "",
      description: "",
      price: 0,
      cost: 0,
      qty: 0,
      qtyMinimal: 0,
      sku: "",
    },
    validationSchema,

    onSubmit: (values) => {
      if (isEmpty(imgs)) {
        toast.info("Selecione uma imagem do produto.");
        return;
      }

      mutate({
        ...values,
        price: unMaskMoney(values.price),
        cost: unMaskMoney(values.cost),
        imgs,
      });
    },
  });

  return (
    <ContainerLoading loading={isPending}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Cadastrar produto"
          subtitle="Cadastre seus produtos aqui."
          back
        />
        <Grid2 size={12}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Input
                id="name"
                name="name"
                label="Nome"
                type="text"
                fullWidth
                size="small"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && !!errors.name}
                helperText={Boolean(touched.name) && Boolean(errors.name)}
              />
            </Grid2>{" "}
            <Grid2 size={6}>
              <Input
                id="sku"
                name="sku"
                label="SKU"
                type="text"
                fullWidth
                size="small"
                value={values.sku}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.sku && !!errors.sku}
                helperText={Boolean(touched.sku) && Boolean(errors.sku)}
              />
            </Grid2>
            <Grid2 size={6}>
              <Input
                id="price"
                name="price"
                label="Preço"
                type="text"
                fullWidth
                size="small"
                value={formatPrice(values.price)}
                onChange={(e) =>
                  setFieldValue("price", maskMoney(e.target.value))
                }
                onBlur={handleBlur}
                error={touched.price && !!errors.price}
                helperText={Boolean(touched.price) && Boolean(errors.price)}
              />
            </Grid2>{" "}
            <Grid2 size={6}>
              <Input
                id="cost"
                name="cost"
                label="Custo ( opcional )"
                type="text"
                fullWidth
                size="small"
                value={formatPrice(values.cost)}
                onChange={(e) =>
                  setFieldValue("cost", maskMoney(e.target.value))
                }
                onBlur={handleBlur}
                error={touched.cost && !!errors.cost}
                helperText={Boolean(touched.cost) && Boolean(errors.cost)}
              />
            </Grid2>
            <Grid2 size={6}>
              <Input
                id="qty"
                name="qty"
                label="Estoque"
                type="text"
                fullWidth
                size="small"
                value={formatNumber(values.qty)}
                onChange={(e) => setFieldValue("qty", e.target.value)}
                onBlur={handleBlur}
                error={touched.qty && !!errors.qty}
                helperText={Boolean(touched.qty) && Boolean(errors.qty)}
              />
            </Grid2>{" "}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Input
                id="qtyMinimal"
                name="cost"
                label="Estoque mínimo ( opcional )"
                type="text"
                fullWidth
                size="small"
                value={formatNumber(values.qtyMinimal)}
                onChange={(e) => setFieldValue("qtyMinimal", e.target.value)}
                onBlur={handleBlur}
                error={touched.qtyMinimal && !!errors.qtyMinimal}
                helperText={
                  Boolean(touched.qtyMinimal) && Boolean(errors.qtyMinimal)
                }
              />
            </Grid2>
            <Grid2 size={12}>
              <Input
                id="description"
                name="description"
                label="Descrição"
                type="text"
                fullWidth
                size="small"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && !!errors.description}
                helperText={
                  Boolean(touched.description) && Boolean(errors.description)
                }
              />
            </Grid2>
            <Grid2 size={12}>
              <DropComponent
                onDrop={(acceptedFiles: File[]) => {
                  const allFiles = [...imgs, ...acceptedFiles].slice(0, 4);
                  setImgs(allFiles);
                }}
                files={imgs}
                setFiles={setImgs}
              />
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Stack mt="2rem" flexDirection={"row"} justifyContent={"flex-end"}>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSubmit()}
            >
              Criar produto
            </Button>
          </Stack>
        </Grid2>
      </Grid2>
    </ContainerLoading>
  );
};

export default ProductsRegister;
